/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Box, Button, Stack } from "@mui/material";
import { defaultRichTextObjectPM } from "cms/components/components/defaults";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import PositionButtons from "components/position-buttons";
import RemoveIcon from "components/remove-icon";
import { SortableContent } from "components/sortable-content";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { cloneObjectsArray } from "utils/array";
import { ComponentCardsSimpleItemContext } from "./component-cards-simple-context";

const defaultItem = Object.freeze({
    image: "", // guid
    description: defaultRichTextObjectPM,
    heading: "", // string
    link: "", // guid
    position: 0 // number
});

/**
 * @param {object} props
 * @param {CardsSimpleItem[]} props.list
 * @param {(items: CardsSimpleItem[]) => void} props.onChange
 * @param {import('react').ReactNode} props.children
 */
const ComponentCardsSimpleEditWrapper = (props) => {
    
    const {
        list: initialList = [],
        onChange,
        children,
    } = props;
    
    const { t } = useTranslation();

    if (typeof onChange !== "function") {
        throw Error("onChange was not supplied, or is not a function");
    }

    const {
        list,
        sortableMethods,
        controls,
    } = useSortableComponentList(
        initialList,
        defaultItem,
        onChange
    );

    const { addNewItem, removeItem, updateItems } = controls;

    const updateItemByPosition = (newItem, position) => {
        const imageArray = cloneObjectsArray(list);
        imageArray[position] = structuredClone(newItem);
        onChange(imageArray);
    };

    return (
        <Fragment>
            <Box style={{ padding: "24px 0" }}>
                <Button
                    size='medium'
                    variant='contained'
                    color={"primary"}
                    target='warning'
                    onClick={addNewItem}
                >
                    {t("commons.addCard")}
                </Button>
            </Box>
            <div className='simplicity-cards-simple-edit-wrapper'>
                <Stack direction='column'>
                    {list &&
                        list.map((item, index) => {
                            const { guid } = item;
                            const ctx = {
                                index,
                                list,
                                onChange,
                                guid,
                                item,
                                position: index,
                                updateItemByPosition,
                                removeItem,
                            };

                            return (
                                <ComponentCardsSimpleItemContext.Provider key={`card-list-${guid}${index}`} value={ctx}>
                                    <SortableContent
                                        id={guid}
                                        index={index}
                                        sortableMethods={sortableMethods}
                                    >
                                        {children}
                                        <div>
                                            <PositionButtons
                                                index={index}
                                                list={list}
                                                handleUpdate={updateItems}
                                            />
                                            <RemoveIcon
                                                className='simplicity-composer-field-editor-remove'
                                                onDeleteItem={removeItem}
                                                index={index}
                                            />
                                        </div>
                                    </SortableContent>
                                    
                                </ComponentCardsSimpleItemContext.Provider>
                            );
                        })}
                </Stack>
            </div>
        </Fragment>
    );
};
export { ComponentCardsSimpleEditWrapper };

/**
 * @typedef {object} CardsSimpleItem
 * @property {string} guid
 * @property {object} description
 * @property {string} heading
 * @property {string} image
 * @property {string} link
 * @property {number} position
 */
